import CallOutlined from '@mui/icons-material/CallOutlined';
import CloudOutlined from '@mui/icons-material/CloudOutlined';
import DoorFrontOutlined from '@mui/icons-material/DoorFrontOutlined';
import NfcRounded from '@mui/icons-material/NfcRounded';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

import { useDeviceDetailsHook } from 'features/device/device-details/hooks/device-details-hook';
import DeviceCommandDetailsPopupSettings from 'features/device/device-details/views/device-commands-detail-settings/device-command-details-popup-settings.component';
import BLEBeaconSettings from 'features/device/device-details/views/device-details-settings/BLEBeacon-settings';

import 'features/device/device-details/views/device-details-settings/device-details-popup-settings.component.scss';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { OrganisationUnitTreeProvider } from 'shared/features/organisation/providers/organisation-unit-tree-provider';
import { useFeatureFlag } from 'shared/hooks/feature-flag';
import { DeviceType, isG2Device } from 'shared/models/device-type';
import { FeatureFlag } from 'shared/models/feature-flag';

import DeviceSettingsOrganisation from 'features/device/device-details/views/device-details-settings/components/device-settings-organisation';
import DomipInputSettings from 'features/device/device-details/views/device-details-settings/components/domip-input-settings.component';
import DomipOutputSettings from 'features/device/device-details/views/device-details-settings/components/domip-output-settings.component';
import IntercomMapping from 'features/device/device-details/views/device-details-settings/components/intercom-mapping/intercom-mapping.component';
import DeviceMonitoring from 'features/device/device-details/views/device-details-settings/device-monitoring.component';
import DeviceSettingsFunction from 'features/device/device-details/views/device-details-settings/device-settings-function';
import { DeviceCommandType } from 'features/device/models/device-command-type';
import { MediaserverAudioSetting } from 'features/mediaserver-settings/domain/models/mediaserver-audio-setting';
import useMediaserverAudioSetting from 'features/mediaserver-settings/hooks/mediaserver-audio-setting-hook';

const DeviceDetailsPopupSettings = (): React.JSX.Element => {
  const { t } = useTranslation('deviceDetails');

  const {
    currentDeviceType,
    currentDevice,
    currentGatewayId,
    currentSystemId,
    currentViewingMode,
    form,
    isMediaserverVideoAvailable,
    getDefaultExpectedRecurrence,
  } = useDeviceDetailsHook();

  const { mediaserverAudioSetting } = useMediaserverAudioSetting();
  const { isEnabled: areIntercomMappingsEnabled } = useFeatureFlag(FeatureFlag.INTERCOM_MAPPINGS);

  const [showMonitoring, setShowMonitoring] = useState<boolean>(currentDevice?.deviceMonitoring?.expectedRecurrence !== undefined);

  const getIndex = (type: DeviceCommandType, defaultIndex: number): number => {
    const index = currentDevice?.commands.findIndex((x) => x.type === type) ?? -1;

    const minimumIndex = currentDevice?.commands.length ?? 0;
    return index !== -1 ? index : minimumIndex + defaultIndex;
  };

  const notViewpointDevices: DeviceType[] = [
    DeviceType.Universal,
    DeviceType.BLEBeacon,
    DeviceType.NFC,
    DeviceType.ESPA,
    DeviceType.UniversalIntercom,
    DeviceType.UniversalCamera,
    DeviceType.UniversalAudio,
    DeviceType.UniversalAlarmTransmitter,
    DeviceType.G2Transmitter,
    DeviceType.G2SmokeAlarm,
    DeviceType.G2MotionDetector,
    DeviceType.G2DoorContact,
    DeviceType.G2TableAlarm,
    DeviceType.G2Contact,
    DeviceType.G2ToiletAlarm,
    DeviceType.DesoResidentTransmitter,
    DeviceType.DesoSupervisorTransmitter,
    DeviceType.DesoDetectionLoop,
  ];

  const disableMonitoringDeviceTypes: DeviceType[] = [
    DeviceType.DesoResidentTransmitter,
    DeviceType.DesoSupervisorTransmitter,
    DeviceType.DesoDetectionLoop,
  ];

  const isViewPointDevice: boolean = notViewpointDevices.includes(currentDeviceType as DeviceType);
  const isMonitoringEnabled = currentDeviceType !== undefined && isG2Device(currentDeviceType) ? true : showMonitoring;

  const getDeviceCommandDetails = (): React.JSX.Element => {
    switch (currentDeviceType) {
      case DeviceType.UniversalAudio:
        return (
          <>
            <DeviceCommandDetailsPopupSettings
              index={getIndex(DeviceCommandType.Audio, 0)}
              commandType={DeviceCommandType.Audio}
              label={t('info.audioHeaderLabel')}
              icon={<CallOutlined />}
            />
            {mediaserverAudioSetting === MediaserverAudioSetting.MediaserverAudioEnabled && (
              <DeviceCommandDetailsPopupSettings
                index={getIndex(DeviceCommandType.AudioMediaserver, 4)}
                commandType={DeviceCommandType.AudioMediaserver}
                label={t('info.audioMediaserverHeaderLabel')}
                icon={<CallOutlined />}
              />
            )}
          </>
        );
      case DeviceType.UniversalCamera:
        return (
          <>
            <DeviceCommandDetailsPopupSettings
              index={getIndex(DeviceCommandType.Video, 2)}
              commandType={DeviceCommandType.Video}
              label={t('info.videoHeaderLabel')}
              icon={<VideocamOutlined />}
            />
            <DeviceCommandDetailsPopupSettings
              index={getIndex(DeviceCommandType.Image, 1)}
              commandType={DeviceCommandType.Image}
              label={t('info.imageHeaderLabel')}
              icon={<VideocamOutlined />}
            />
            {isMediaserverVideoAvailable && (
              <DeviceCommandDetailsPopupSettings
                index={getIndex(DeviceCommandType.Rtsp, 3)}
                commandType={DeviceCommandType.Rtsp}
                label={t('info.rtspHeaderLabel')}
                icon={<VideocamOutlined />}
              />
            )}
            <DeviceCommandDetailsPopupSettings
              index={getIndex(DeviceCommandType.Audio, 0)}
              commandType={DeviceCommandType.Audio}
              label={t('info.audioHeaderLabel')}
              icon={<CallOutlined />}
            />
            {mediaserverAudioSetting === MediaserverAudioSetting.MediaserverAudioEnabled && (
              <DeviceCommandDetailsPopupSettings
                index={getIndex(DeviceCommandType.AudioMediaserver, 4)}
                commandType={DeviceCommandType.AudioMediaserver}
                label={t('info.audioMediaserverHeaderLabel')}
                icon={<CallOutlined />}
              />
            )}
          </>
        );
      case DeviceType.UniversalIntercom:
        return (
          <>
            <DeviceCommandDetailsPopupSettings
              index={getIndex(DeviceCommandType.OpenDoor, 2)}
              commandType={DeviceCommandType.OpenDoor}
              label={t('info.doorHeaderLabel')}
              icon={<DoorFrontOutlined />}
            />
            <DeviceCommandDetailsPopupSettings
              index={getIndex(DeviceCommandType.Video, 1)}
              commandType={DeviceCommandType.Video}
              label={t('info.videoHeaderLabel')}
              icon={<VideocamOutlined />}
            />
            {isMediaserverVideoAvailable && (
              <DeviceCommandDetailsPopupSettings
                index={getIndex(DeviceCommandType.Rtsp, 3)}
                commandType={DeviceCommandType.Rtsp}
                label={t('info.rtspHeaderLabel')}
                icon={<VideocamOutlined />}
              />
            )}
            <DeviceCommandDetailsPopupSettings
              index={getIndex(DeviceCommandType.Audio, 0)}
              commandType={DeviceCommandType.Audio}
              label={t('info.audioHeaderLabel')}
              icon={<CallOutlined />}
            />
            {mediaserverAudioSetting === MediaserverAudioSetting.MediaserverAudioEnabled && (
              <DeviceCommandDetailsPopupSettings
                index={getIndex(DeviceCommandType.AudioMediaserver, 4)}
                commandType={DeviceCommandType.AudioMediaserver}
                label={t('info.audioMediaserverHeaderLabel')}
                icon={<CallOutlined />}
              />
            )}
          </>
        );
      case DeviceType.DomipOutput:
        return <DomipOutputSettings />;
      default:
        return <></>;
    }
  };

  const showDeviceSettings = (): boolean => {
    switch (currentDeviceType) {
      case DeviceType.UniversalAudio:
      case DeviceType.UniversalCamera:
      case DeviceType.UniversalIntercom:
      case DeviceType.DomipOutput:
        return true;
      default:
        return false;
    }
  };

  const shouldShowMonitoring =
    isViewPointDevice &&
    !disableMonitoringDeviceTypes.includes(currentDeviceType as DeviceType) &&
    (currentViewingMode !== 'viewing' ||
      (currentViewingMode === 'viewing' && currentDevice?.deviceMonitoring?.expectedRecurrence !== undefined));

  useEffect(() => {
    if (currentViewingMode === 'viewing') {
      setShowMonitoring(currentDevice?.deviceMonitoring?.expectedRecurrence !== undefined);
    } else {
      form.setValue('deviceMonitoring.expectedRecurrence', isMonitoringEnabled ? getDefaultExpectedRecurrence() : undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentViewingMode, currentDevice, currentDeviceType]);

  return (
    <div className="device-details-popup-settings-container">
      {currentDeviceType && currentDeviceType !== DeviceType.Unknown && (
        <>
          <div className="settings-item" data-testid="deviceOrganisationContainer">
            <Typography variant="h5">{t('settings.organisationUnitLabel')}</Typography>
            <div className="settings-item-content">
              <OrganisationUnitTreeProvider.ForLinkedIntegration gatewayId={currentGatewayId} systemId={currentSystemId}>
                <DeviceSettingsOrganisation />
              </OrganisationUnitTreeProvider.ForLinkedIntegration>
            </div>
          </div>
          {currentDeviceType === DeviceType.NFC && (
            <>
              <Divider />
              <div className="settings-item" data-testid="deviceTypeContainer">
                <Typography variant="h5">{t('info.deviceSettingsLabel')}</Typography>
                <div className="icon-text">
                  <NfcRounded width={24} height={24} />
                  <Typography variant="h5">{t('NFC.label')}</Typography>
                </div>
                <DeviceSettingsFunction />
              </div>
            </>
          )}
          {currentDeviceType === DeviceType.DoorAlarm && (
            <>
              <Divider />
              <div className="settings-item" data-testid="deviceTypeContainer">
                <Typography variant="h5">{t('info.deviceSettingsLabel')}</Typography>
                <div className={'events'}>
                  <Typography variant="subtitle1">{t('doorFunctions.open')}</Typography>
                  <Typography variant="subtitle1">{t('doorFunctions.closed')}</Typography>
                </div>
              </div>
            </>
          )}
          {currentDeviceType === DeviceType.BLEBeacon && <BLEBeaconSettings />}
          <Divider />
          {shouldShowMonitoring && (
            <>
              <div className="settings-item" data-testid="deviceMonitoringContainer">
                <div className="header-row">
                  <div className="icon-text">
                    <CloudOutlined width={24} height={24} />
                    <Typography variant="h5">{t('info.monitoring')}</Typography>
                  </div>
                  <Switch
                    type="checkbox"
                    disabled={currentViewingMode === 'viewing' || isG2Device(currentDeviceType) ? true : false}
                    checked={isMonitoringEnabled}
                    onChange={(_, checked) => {
                      setShowMonitoring(checked);
                      form.setValue('deviceMonitoring.expectedRecurrence', checked ? getDefaultExpectedRecurrence() : undefined, {
                        shouldDirty: true,
                      });
                    }}
                  />
                </div>
                {isMonitoringEnabled && <DeviceMonitoring />}
              </div>
              <Divider />
            </>
          )}
          {currentDeviceType === DeviceType.DomipInput && <DomipInputSettings />}
          {currentDeviceType === DeviceType.UniversalIntercom && areIntercomMappingsEnabled && <IntercomMapping />}
          {showDeviceSettings() && (
            <div className="settings-item full-width" data-testid="deviceCommandsContainer">
              <Typography variant="h5">{t('info.deviceSettingsLabel')}</Typography>
              <div className="settings-item-content full-width">{getDeviceCommandDetails()}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DeviceDetailsPopupSettings;
